import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, addDoc, getDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { useRole } from '../hooks/useRole';
import { useData } from '../context/DataContext';
import { useTheme } from '../context/ThemeContext';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';
import { resolveInventoryStatus } from '../utils/inventoryLedger';
import { pickBadge, hasBackOrder, backOrderCount, collectableCount } from '../utils/pickStatus';
import { normalizeSearch } from '../utils/searchMatch';
import { BOARD_COLUMNS, BOARD_STATUS_OPTIONS } from '../utils/boardColumns';
import { getMentionableNames } from '../data/teamMembers';
import AnnouncementModal from '../components/AnnouncementModal';
import {
  fetchActiveAnnouncements, fetchDismissedIds, dismissAnnouncement,
  unseenAnnouncements, relativeTime,
} from '../utils/announcements';

// Blue palette — flat iOS look (was cyan/violet dark theme)
const C_CYAN = '#0077B6';          // primary accent (main buttons, primary numbers, quotes)
const C_CYAN_BRIGHT = '#0077B6';   // primary accent (bright variant unified)
const C_VIOLET = '#00B4D8';        // secondary accent (invoices, secondary highlights)
const C_VIOLET_BRIGHT = '#00B4D8'; // secondary accent
const C_GREEN = '#10b981';         // semantic: online / healthy (unchanged)
const C_ORANGE = '#EF9F27';        // semantic: low-stock / warning amber
const C_RED = '#E24B4A';           // semantic: out-of-stock / error red
const C_PURPLE = '#7C3AED';        // Install Jobs board column
const C_PINK = '#C2255C';          // Special Order Items board column
// Board search highlight. Deliberately NOT one of the column colours: a match
// has to read as "the search found this", not as "this card changed column".
const BOARD_MATCH_INK = '#B45309';
const BOARD_MATCH_BG = '#FDF3D8';
const C_YELLOW = '#E9C46A';        // Not Ordered / Pending board column (soft yellow)

const C_BG = '#CDE7F5';            // page background (soft blue-gray)
const C_BG_CARD = '#FFFFFF';       // card background (white)
const C_BG_CARD_SOLID = '#FFFFFF';
const C_BG_NESTED = '#E6F1FB';     // soft blue tint (nested rows, inputs, badges)
const C_BORDER = '#E3ECF5';        // hairline border
const C_BORDER_CYAN = '#0077B6';   // accent border (focus / hover)

const C_TEXT = '#03045E';          // primary text / titles (dark navy)
const C_TEXT_2 = '#6B7C93';        // secondary text (soft slate)
const C_TEXT_3 = '#9AA8BC';        // tertiary text (light slate)

const GRADIENT_HERO = '#0077B6';   // flat primary accent (formerly cyan→violet gradient)

// Squash an announcement body onto one flowing run for the card preview. The
// stored text is untouched — this is display only, and the modal shows the
// paragraphs as written.
const previewText = (body) => String(body || '').replace(/\s+/g, ' ').trim();

// How many announcements the card lists before it says how many more there are.
// Five keeps the card about the height of a busy Today's Invoices column.
const ANNOUNCEMENTS_ON_CARD = 5;

// ── THE BOARD'S COLUMNS ──────────────────────────────────────────────
//
// Ids, labels and ORDER come from utils/boardColumns.js — the one list every
// status dropdown in the app also reads (see the notes there on why a column's
// stored id must never be renamed). Only the colours are the board's own.
const COLUMN_COLORS = {
  'Not Ordered':         { color: C_YELLOW, accent: '#8A6D1F' },
  'Ordered':             { color: C_CYAN,   accent: C_CYAN_BRIGHT },
  'Special Order Items': { color: C_PINK,   accent: '#8C1741' },
  'Deliveries':          { color: C_ORANGE, accent: '#854F0B' },
  'Back Order':          { color: C_RED,    accent: '#A32D2D' },
  'Picked Up':           { color: C_GREEN,  accent: '#0F6E56' },
  'Hoco/Fallston Jobs':  { color: C_PURPLE, accent: '#5B21B6' },
};
const COLUMNS = BOARD_COLUMNS.map(c => {
  const { color, accent } = COLUMN_COLORS[c.id] || { color: C_CYAN, accent: C_CYAN_BRIGHT };
  return { ...c, color, accent, borderLeft: color };
});

// The statuses the board asks Firestore for. Derived from COLUMNS rather than
// written out again: a column that existed here but not in the query would show
// up empty for ever, which is exactly the sort of quiet disagreement two
// hand-maintained lists produce.
const BOARD_STATUSES = COLUMNS.map(c => c.id);
// Picked Up is fetched separately, and only for the last seven days.
const ACTIVE_STATUSES = BOARD_STATUSES.filter(id => id !== 'Picked Up');
// A fresh, empty bucket per column.
const emptyBoard = () => Object.fromEntries(BOARD_STATUSES.map(id => [id, []]));

// ── BOARD SEARCH ─────────────────────────────────────────────────────
//
// IT HIGHLIGHTS, IT DOES NOT FILTER. A board is a picture of where the work is,
// and hiding cards destroys that picture — six of seven columns going empty
// reads as "those orders are gone", and somebody rings the office about an
// invoice that was on screen a moment ago. Everything stays put; the matches
// are marked.
//
// The forgiveness rules are lifted from the Shop Drawings search, deliberately
// unchanged, so one search box in this app behaves like another:
//
//   1. Plain case-insensitive substring, which keeps a partial like "26-00"
//      working, punctuation and all.
//   2. The same with punctuation stripped from BOTH sides, so the hyphens in a
//      document number stop mattering. This is what makes 26-006389, 006389 and
//      6389 all find INV-26-006389.
//   3. An all-digits query compared NUMERICALLY against the trailing sequence,
//      so leading zeros need not be typed.
// normalizeSearch comes from utils/searchMatch.js — the same single copy the
// Shop Drawings search and the Documents page use.

/** Document number and customer name — the two things anyone looks a card up by. */
const boardSearchFields = (inv) => [inv?.docNumber, inv?.customerName];

export function boardCardMatches(invoice, rawQuery) {
  const trimmed = String(rawQuery ?? '').trim();
  if (!trimmed) return false;   // no query, nothing highlighted

  const fields = boardSearchFields(invoice);

  const lower = trimmed.toLowerCase();
  if (fields.some(f => String(f ?? '').toLowerCase().includes(lower))) return true;

  const nq = normalizeSearch(trimmed);
  if (nq && fields.some(f => normalizeSearch(f).includes(nq))) return true;

  if (/^\d+$/.test(trimmed)) {
    const sequence = String(invoice?.docNumber ?? '').split('-').pop();
    if (/^\d+$/.test(sequence) && parseInt(sequence, 10) === parseInt(trimmed, 10)) return true;
  }
  return false;
}

// The card modal's status dropdown. Straight from COLUMNS so the two can never
// drift: the thing that WRITES the status and the thing that READS it were
// previously two hand-kept lists, and a value in one but not the other is a
// card that can be moved somewhere the board will not show it.
const STATUS_OPTIONS = BOARD_STATUS_OPTIONS;

// Stock-level progress row (ported from Reports.js, restyled to the blue/light palette).
// Bars stay semantic: red for out-of-stock, amber for low-stock.
function StockProgressRow({ product, type }) {
  const stock = parseFloat(product.stockQty) || 0;
  const reorder = parseFloat(product.reorderPoint) || 1;
  const percent = type === 'out' ? 0 : Math.max(0, Math.min(100, (stock / reorder) * 100));
  const barColor = type === 'out' ? '#E24B4A' : '#EF9F27';
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #E3ECF5' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: '#03045E', fontSize: 13, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {product.description}
          </p>
          <p style={{ color: '#6B7C93', fontSize: 10, margin: '2px 0 0 0' }}>
            <span style={{ color: '#0077B6', fontFamily: 'monospace', fontWeight: 700 }}>{product.sku}</span>
          </p>
        </div>
        <p style={{ color: barColor, fontSize: 13, fontWeight: 700, marginLeft: 12, whiteSpace: 'nowrap' }}>
          {stock} / {reorder}
        </p>
      </div>
      <div style={{ height: 6, background: '#F0F5FA', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${percent}%`, background: barColor, borderRadius: 3, transition: 'width 0.5s' }} />
      </div>
    </div>
  );
}

// ── WHO CAN BE @MENTIONED ──
//
// The live list, from RoleContext: everyone in the Firestore `teamRoles`
// collection MERGED with everyone in data/teamMembers.js, deduplicated. So a
// person added in Settings can be mentioned without a deploy.
//
// This used to be computed ONCE at module load from the file alone, which meant
// a new hire could never be mentioned. It is now read per render from the
// context, which re-resolves when the Firestore read lands or Settings writes.
//
// A SLOW OR FAILED READ IS NOT A BROKEN PICKER: the merge always includes the
// file's five names, so the worst case is exactly the old behaviour.
//
// The file-only list stays as the fallback for the two module-scope helpers
// below, which have no access to a hook.
const FILE_MENTIONABLE_NAMES = getMentionableNames();

// A mention token: an @ that starts a word, followed by name characters.
// Dots, hyphens and underscores are allowed INSIDE a token so an address like
// mary.jane@ could ever be mentioned, but trailing ones are stripped below so
// that "ask @STEVE." still resolves to STEVE rather than to "STEVE.".
const MENTION_TOKEN_RE = /(^|\s)@([A-Za-z0-9._-]+)/g;

/**
 * Pull the @mentions out of a comment and resolve them against the team list.
 *
 * Matching is case-insensitive going in and uppercased coming out, because the
 * stored form has to be the same identifier everything else uses. Unknown names
 * are dropped — an @mention of someone who is not on the team is just text, not
 * a notification with nowhere to go. Duplicates collapse, so "@steve and
 * @Steve" yields STEVE once, in order of first appearance.
 */
function parseMentions(text, names) {
  // Defaults to the file's names so a caller without the live list — or a read
  // that has not landed — still resolves the five known people rather than
  // dropping every mention on the floor.
  const known = new Set(Array.isArray(names) && names.length ? names : FILE_MENTIONABLE_NAMES);
  const found = [];
  const source = String(text || '');
  let match;
  MENTION_TOKEN_RE.lastIndex = 0;
  while ((match = MENTION_TOKEN_RE.exec(source)) !== null) {
    const name = match[2].replace(/[._-]+$/, '').toUpperCase();
    if (known.has(name) && !found.includes(name)) found.push(name);
  }
  return found;
}

/**
 * Deep equality for a LEGACY comment — one written before the id field existed,
 * carrying only text, author and createdAt.
 *
 * This is the same test arrayRemove applies, spelled out by field rather than
 * by JSON.stringify, because objects coming back from Firestore carry no
 * guaranteed key order and a stringify comparison would be fragile for it.
 */
const sameLegacyComment = (a, b) =>
  !!a && !!b && !a.id && !b.id
  && a.text === b.text && a.author === b.author && a.createdAt === b.createdAt;

// Unique id for a new board comment.
//
// This exists because of arrayUnion. arrayUnion appends server-side — which is
// the whole point, it removes the read-modify-write window — but it also
// DEDUPLICATES by deep equality. Two byte-identical comment objects collapse
// into one, and a double-clicked submit is exactly the case that produces them:
// same author, same text, and potentially the same millisecond in createdAt.
// A unique id per comment makes every object distinct, so nothing is swallowed.
//
// crypto.randomUUID needs a secure context (https / localhost), which is where
// this app runs. The fallback is there only so a comment can never be lost to a
// ReferenceError somewhere unexpected; it is still unique enough that arrayUnion
// will not fold two rapid submissions together.
const newCommentId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Trailing zeros dropped, two decimals kept — quarter inches must survive, and
// the drawing list has to read the same as the drawing does.
const fmtIn = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `${Math.round(n * 100) / 100}"` : '—';
};

function CardModal({ invoice, drawings, onOpenDrawing, onClose, onNavigate, onStatusChange, onAddComment, onDeleteComment, currentUser }) {
  // Everyone who can be @mentioned, live from the role system.
  const { mentionableNames } = useRole();
  // Read only here as well — the card reports what the shop has done, it does
  // not change it. Sending an invoice to the shop lives in ViewDocument.
  const pick = pickBadge(invoice);
  const C_BG_CARD_SOLID = '#FFFFFF';
  const C_BG_NESTED = '#E6F1FB';
  const C_BORDER = '#E3ECF5';
  const C_BORDER_CYAN = '#0077B6';
  const C_TEXT = '#03045E';
  const C_TEXT_2 = '#6B7C93';
  const C_TEXT_3 = '#9AA8BC';
  const C_CYAN = '#0077B6';
  const C_CYAN_BRIGHT = '#0077B6';
  const C_VIOLET = '#00B4D8';
  const C_RED = '#A32D2D';
  const GRADIENT_HERO = '#0077B6';

  const [commentText, setCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // ── @mention picker ──
  // The input is a single line, so the picker tracks the caret itself: mentionAt
  // is the index of the '@' that opened it, mentionQuery is what has been typed
  // since. Both are needed to splice the completed name back into the middle of
  // whatever else the user has written.
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionAt, setMentionAt] = useState(-1);
  const [mentionIndex, setMentionIndex] = useState(0);
  const commentInputRef = useRef(null);

  // The LIVE list — see the note at FILE_MENTIONABLE_NAMES. It already contains
  // the file's names whatever Firestore did, so the picker can never be empty
  // because of a slow or failed read.
  const mentionMatches = mentionOpen
    ? mentionableNames.filter(n => n.startsWith(mentionQuery.toUpperCase()))
    : [];
  // The dropdown is only ever shown when it has something to offer, and the key
  // handling below keys off the same flag — so with no matches, Enter submits
  // exactly as it always did rather than being swallowed by an invisible list.
  const mentionVisible = mentionOpen && mentionMatches.length > 0;

  const closeMention = () => { setMentionOpen(false); setMentionAt(-1); setMentionQuery(''); setMentionIndex(0); };

  // Decide, from the text and caret position, whether an @mention is in
  // progress. The @ has to START a word, and the fragment after it cannot
  // contain whitespace — otherwise an address typed into a comment, or an @
  // left behind earlier in the line, would reopen the picker.
  const syncMention = (value, caret) => {
    const upToCaret = value.slice(0, caret);
    const at = upToCaret.lastIndexOf('@');
    if (at === -1) { closeMention(); return; }
    const charBefore = at === 0 ? '' : upToCaret[at - 1];
    if (charBefore && !/\s/.test(charBefore)) { closeMention(); return; }
    const fragment = upToCaret.slice(at + 1);
    if (/\s/.test(fragment)) { closeMention(); return; }
    setMentionAt(at);
    setMentionQuery(fragment);
    setMentionIndex(0);
    setMentionOpen(true);
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setCommentText(value);
    syncMention(value, e.target.selectionStart ?? value.length);
  };

  // Replace the partial "@que" with the full "@NAME " and drop the caret after
  // the trailing space, so the user can keep typing without repositioning.
  const insertMention = (name) => {
    if (mentionAt < 0) return;
    const el = commentInputRef.current;
    const caret = el?.selectionStart ?? commentText.length;
    const next = commentText.slice(0, mentionAt) + '@' + name + ' ' + commentText.slice(caret);
    const nextCaret = mentionAt + name.length + 2;
    setCommentText(next);
    closeMention();
    // The DOM value updates on the next render, so the caret move waits a tick.
    setTimeout(() => {
      const node = commentInputRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(nextCaret, nextCaret);
    }, 0);
  };

  const handleCommentKeyDown = (e) => {
    if (mentionVisible) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(i => (i + 1) % mentionMatches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(i => (i - 1 + mentionMatches.length) % mentionMatches.length);
        return;
      }
      // Enter SELECTS while the picker is open — it must not also submit the
      // comment, which is what would otherwise happen on the very keystroke
      // that completes a name.
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionMatches[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') { e.preventDefault(); closeMention(); return; }
    }
    if (e.key === 'Escape' && mentionOpen) { closeMention(); return; }
    if (e.key === 'Enter' && !e.shiftKey) handleAddComment();
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setAddingComment(true);
    closeMention();
    await onAddComment(invoice, commentText.trim());
    setCommentText('');
    setAddingComment(false);
  };

  const columnColor = COLUMNS.find(c => c.id === (invoice.status || 'Ordered'))?.borderLeft || C_CYAN;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: C_BG_CARD_SOLID,
          border: `1px solid ${C_BORDER_CYAN}`,
          borderRadius: 20,
          boxShadow: '0 20px 50px rgba(3, 4, 94, 0.18)',
          width: '100%', maxWidth: 520,
          maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          color: C_TEXT,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: 20, borderBottom: `1px solid ${C_BORDER}`,
        }}>
          <div>
            <p style={{ color: C_CYAN_BRIGHT, fontFamily: 'monospace', fontWeight: 700, fontSize: 13, margin: 0 }}>
              #{invoice.docNumber}
            </p>
            <h3 style={{ color: C_TEXT, fontSize: 18, fontWeight: 700, marginTop: 4, marginBottom: 0 }}>
              {invoice.customerName}
            </h3>
            <p style={{
              fontSize: 22, fontWeight: 800,
              color: '#0077B6',
              marginTop: 4, marginBottom: 0,
            }}>
              ${parseFloat(invoice.grandTotal || 0).toFixed(2)}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', color: C_TEXT_3,
              fontSize: 24, lineHeight: 1, cursor: 'pointer', marginLeft: 16,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = C_TEXT; }}
            onMouseLeave={e => { e.currentTarget.style.color = C_TEXT_3; }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div className="dash-scroll" style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {/* Meta */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C_TEXT_2, marginBottom: 16 }}>
            <span>👤 {invoice.salesperson}</span>
            <span>
              {invoice.date ? new Date(invoice.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
            </span>
          </div>

          {/* Status dropdown */}
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block', fontSize: 11, fontWeight: 700, color: C_TEXT_3,
              textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6,
            }}>
              Board Status
            </label>
            <select
              value={invoice.status || 'Ordered'}
              onChange={e => onStatusChange(invoice, e.target.value)}
              style={{
                width: '100%',
                background: C_BG_NESTED,
                border: `1px solid ${columnColor}66`,
                borderRadius: 10,
                padding: '8px 12px',
                fontSize: 14, fontWeight: 600,
                color: columnColor,
                outline: 'none',
              }}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value} style={{ color: '#111827', background: '#fff' }}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {pick && (
            <div style={{
              background: pick.bg, color: pick.fg,
              borderRadius: 12, padding: '10px 14px', marginBottom: 16,
              fontSize: 13, fontWeight: 700,
            }}>
              {/* Straight from PICK_STATUS, so a wording change reaches here
                  too rather than leaving one screen saying something else. */}
              {pick.icon} {pick.label}
              <span style={{ fontWeight: 500, opacity: 0.85 }}>
                {pick.key === 'Ready'
                  ? `  ·  picked by ${invoice.pickStatusBy || '—'}`
                  : `  ·  sent by ${invoice.pickRequestedBy || '—'}`}
              </span>
            </div>
          )}

          {/* Back order, stated separately from the pick status above for the
              same reason it is separate on the card: they answer two different
              questions and merging them loses one of the answers. */}
          {hasBackOrder(invoice) && (
            <div style={{
              background: 'transparent', color: '#8A3D00',
              border: '1.5px solid #C77700',
              borderRadius: 12, padding: '10px 14px', marginBottom: 16,
              fontSize: 13, fontWeight: 700,
            }}>
              ⬚ Back order
              <span style={{ fontWeight: 500 }}>
                {`  ·  ${backOrderCount(invoice)} of ${collectableCount(invoice)} lines not yet collected`}
              </span>
            </div>
          )}

          <div style={{ borderTop: `1px solid ${C_BORDER}`, marginBottom: 16 }} />

          {/* Comments */}
          <div>
            <label style={{
              display: 'block', fontSize: 11, fontWeight: 700, color: C_TEXT_3,
              textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8,
            }}>
              Comments {invoice.boardComments?.length > 0 && `(${invoice.boardComments.length})`}
            </label>

            <div style={{ marginBottom: 12 }}>
              {(!invoice.boardComments || invoice.boardComments.length === 0) ? (
                <p style={{
                  color: C_TEXT_3, fontSize: 13, textAlign: 'center',
                  padding: 16, background: C_BG_NESTED, borderRadius: 12, margin: 0,
                }}>
                  No comments yet
                </p>
              ) : (
                // Comments written before the id field existed have none, so the
                // array index remains the fallback key. They are not migrated.
                invoice.boardComments.map((c, i) => (
                  <div key={c.id || i} style={{
                    background: C_BG_NESTED, borderRadius: 12, padding: 12, marginBottom: 8,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ color: C_CYAN_BRIGHT, fontWeight: 700, fontSize: 12 }}>{c.author}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: C_TEXT_3, fontSize: 11 }}>
                          {new Date(c.createdAt).toLocaleString('en-US', {
                            month: 'short', day: 'numeric',
                            hour: 'numeric', minute: '2-digit',
                          })}
                        </span>
                        {/* Own comments only. There is no admin override — a
                            comment is a record of what someone said, and nobody
                            gets to remove somebody else's. The handler repeats
                            the same author check, so hiding the control is the
                            affordance rather than the rule. Same treatment as
                            the modal's close button: bare glyph, muted until
                            hovered. */}
                        {c.author === currentUser && (
                          <button
                            onClick={() => onDeleteComment(invoice, c, i)}
                            title="Delete your comment"
                            aria-label="Delete your comment"
                            style={{
                              background: 'transparent', border: 'none', padding: 0,
                              color: C_TEXT_3, fontSize: 12, lineHeight: 1,
                              cursor: 'pointer', fontFamily: 'inherit',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.color = C_RED; }}
                            onMouseLeave={e => { e.currentTarget.style.color = C_TEXT_3; }}
                          >
                            🗑
                          </button>
                        )}
                      </span>
                    </div>
                    <p style={{ color: C_TEXT_2, fontSize: 13, margin: 0 }}>{c.text}</p>
                  </div>
                ))
              )}
            </div>

            {/* Add comment */}
            <div style={{ display: 'flex', gap: 8 }}>
              {/* Relatively positioned so the mention picker can anchor to the
                  input. It opens UPWARD — this row sits at the bottom of the
                  modal, and a downward list would fall off the panel. */}
              <div style={{ flex: 1, position: 'relative' }}>
              <input
                ref={commentInputRef}
                type="text"
                value={commentText}
                onChange={handleCommentChange}
                onKeyDown={handleCommentKeyDown}
                onClick={e => syncMention(e.target.value, e.target.selectionStart ?? 0)}
                placeholder="Add a comment…  @ to mention"
                style={{
                  width: '100%',
                  background: C_BG_NESTED,
                  border: `1px solid ${C_BORDER}`,
                  borderRadius: 10,
                  padding: '8px 12px', fontSize: 13,
                  color: C_TEXT, outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={e => { e.target.style.borderColor = C_BORDER_CYAN; }}
                onBlur={e => { e.target.style.borderColor = C_BORDER; }}
              />

              {/* Same treatment as the customer picker elsewhere in the app:
                  absolutely positioned panel, hairline border, soft shadow,
                  scrolling list of left-aligned buttons that tint on hover. */}
              {mentionVisible && (
                <div style={{
                  position: 'absolute', zIndex: 100, bottom: '100%', left: 0, right: 0, marginBottom: 6,
                  background: C_BG_CARD_SOLID, border: `1px solid ${C_BORDER}`, borderRadius: 12,
                  maxHeight: 240, overflowY: 'auto', boxShadow: '0 12px 30px rgba(3, 4, 94, 0.12)',
                }}>
                  {mentionMatches.map((name, i) => (
                    <button
                      key={name}
                      type="button"
                      // mousedown fires before the input's blur, so preventing
                      // it keeps focus and the caret where they are.
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => insertMention(name)}
                      onMouseEnter={() => setMentionIndex(i)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        background: i === mentionIndex ? C_BG_NESTED : 'transparent',
                        border: 'none', borderBottom: `1px solid ${C_BORDER}`, padding: '10px 14px',
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      <span style={{ color: C_TEXT, fontWeight: 600, fontSize: 14 }}>@{name}</span>
                    </button>
                  ))}
                </div>
              )}
              </div>
              <button
                onClick={handleAddComment}
                disabled={addingComment || !commentText.trim()}
                style={{
                  background: GRADIENT_HERO, color: C_TEXT,
                  fontWeight: 700, fontSize: 13,
                  padding: '8px 16px', borderRadius: 10,
                  border: 'none', cursor: addingComment || !commentText.trim() ? 'default' : 'pointer',
                  opacity: addingComment || !commentText.trim() ? 0.4 : 1,
                  flexShrink: 0,
                }}
              >
                {addingComment ? '…' : 'Add'}
              </button>
            </div>
          </div>
        </div>

        {/* Linked drawings — listed here because the card can only afford a
            count. Number, gate type and size, which is what identifies a gate
            to somebody looking for one. */}
        {(drawings || []).length > 0 && (
          <div style={{ padding: '0 20px 16px 20px' }}>
            <p style={{ color: C_TEXT_2, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, margin: '0 0 8px 0' }}>
              SHOP DRAWINGS
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {drawings.map(d => (
                <button
                  key={d.id}
                  onClick={() => onOpenDrawing(d.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    textAlign: 'left', width: '100%',
                    background: C_BG_NESTED, border: `1px solid ${C_BORDER}`,
                    borderRadius: 10, padding: '8px 12px',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#5B21B6', fontSize: 12 }}>
                    {d.drawingNumber || '—'}
                  </span>
                  <span style={{ color: C_TEXT, fontSize: 12 }}>{d.type || '—'}</span>
                  <span style={{ color: C_TEXT_3, fontSize: 12 }}>
                    {fmtIn(d.openingIn)} × {fmtIn(d.heightIn)}
                  </span>
                  <span style={{ color: C_TEXT_3, fontSize: 11, marginLeft: 'auto' }}>Open →</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 12, padding: 20, borderTop: `1px solid ${C_BORDER}` }}>
          <button
            onClick={() => onNavigate(invoice.id)}
            style={{
              flex: 1, background: GRADIENT_HERO, color: C_TEXT,
              fontWeight: 700, fontSize: 13,
              padding: '10px 16px', borderRadius: 12,
              border: 'none', cursor: 'pointer',
            }}
          >
            Open Invoice →
          </button>
          <button
            onClick={onClose}
            style={{
              background: C_BG_NESTED, color: C_TEXT_2,
              fontWeight: 700, fontSize: 13,
              padding: '10px 16px', borderRadius: 12,
              border: `1px solid ${C_BORDER}`, cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const DASHBOARD_CACHE_KEY = 'afs_dashboard_cache';
const CACHE_TTL = 5 * 60 * 1000;

function loadDashboardCache() {
  try {
    const raw = sessionStorage.getItem(DASHBOARD_CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < CACHE_TTL) return data;
    return null;
  } catch { return null; }
}

function saveDashboardCache(data) {
  try {
    sessionStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {}
}

const DAILY_TASKS_DEFAULT = [
  'Review pending quotes',
  'Follow up on invoices',
  'Check inventory',
  'Update price list',
];

function Dashboard({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isSalesperson, userName: roleName, loading: roleLoading, mentionableNames } = useRole();
  const { products, customers } = useData();
  const { theme, themeName, toggleTheme } = useTheme();

  // Theme-derived constants — these SHADOW the module-level dark defaults
  // and make the Dashboard body switch with the toggle. Sub-components
  // (KanbanCard, KanbanColumn, CardModal) still read module-level dark
  // constants for now — they'll be made theme-aware in Phase 2.
  const C_BG = '#CDE7F5';
  const C_BG_CARD = '#FFFFFF';
  const C_BG_CARD_SOLID = '#FFFFFF';
  const C_BG_NESTED = '#E6F1FB';
  const C_BORDER = '#E3ECF5';
  const C_BORDER_CYAN = '#0077B6';
  const C_TEXT = '#03045E';
  const C_TEXT_2 = '#6B7C93';
  const C_TEXT_3 = '#9AA8BC';

  const displayName = roleName || user?.email?.split('@')[0]?.toUpperCase();

  const [rawTodayQuotes, setRawTodayQuotes] = useState([]);
  const [rawTodayInvoices, setRawTodayInvoices] = useState([]);
  const [rawThisMonthInvoices, setRawThisMonthInvoices] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [boardInvoices, setBoardInvoices] = useState(emptyBoard);
  const [boardSearch, setBoardSearch] = useState('');
  // Linked drawings, keyed by the invoice they belong to. See loadBoardDrawings.
  const [boardDrawings, setBoardDrawings] = useState({});
  const [boardRefreshing, setBoardRefreshing] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  // ── Announcements ──
  // Active list for the card, plus the ids THIS user has dismissed. Both are
  // read once on mount: an announcement is not time-critical, and a listener
  // here would be the app's second one for no benefit.
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [popupItems, setPopupItems] = useState(null);
  // An announcement opened BY HAND from the card. Separate state from
  // popupItems so the reader can never be confused with the login queue —
  // opening this one marks nothing seen.
  const [readingAnnouncement, setReadingAnnouncement] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tasks, setTasks] = useState(() => {
    try {
      const saved = localStorage.getItem('afs_daily_tasks');
      return saved ? JSON.parse(saved) : DAILY_TASKS_DEFAULT.map(label => ({ label, done: false }));
    } catch {
      return DAILY_TASKS_DEFAULT.map(label => ({ label, done: false }));
    }
  });
  const [editingTasks, setEditingTasks] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  // Load stats (today's quotes/invoices + this month)
  useEffect(() => {
    const load = async () => {
      const cached = loadDashboardCache();
      if (cached) {
        setRawTodayQuotes(cached.todayQuotes);
        setRawTodayInvoices(cached.todayInvoices);
        setRawThisMonthInvoices(cached.thisMonthInvoices);
        setLoadingDocs(false);
        return;
      }

      setLoadingDocs(true);
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      try {
        const [qTodaySnap, iTodaySnap, iMonthSnap] = await Promise.all([
          getDocs(query(collection(db, 'quotes'), where('createdAt', '>=', todayStart), orderBy('createdAt', 'desc'))),
          getDocs(query(collection(db, 'invoices'), where('createdAt', '>=', todayStart), orderBy('createdAt', 'desc'))),
          getDocs(query(collection(db, 'invoices'), where('createdAt', '>=', monthStart), orderBy('createdAt', 'desc'))),
        ]);

        const todayQuotes = qTodaySnap.docs.map(d => ({ ...d.data(), id: d.id }));
        const todayInvoices = iTodaySnap.docs.map(d => ({ ...d.data(), id: d.id }));
        const thisMonthInvoices = iMonthSnap.docs.map(d => ({ ...d.data(), id: d.id }));

        setRawTodayQuotes(todayQuotes);
        setRawTodayInvoices(todayInvoices);
        setRawThisMonthInvoices(thisMonthInvoices);
        saveDashboardCache({ todayQuotes, todayInvoices, thisMonthInvoices });
      } catch (err) {
        console.error('Error loading dashboard data:', err);
      }
      setLoadingDocs(false);
    };
    load();
  }, []);

  useEffect(() => {
    localStorage.setItem('afs_daily_tasks', JSON.stringify(tasks));
  }, [tasks]);

  // Load kanban board invoices
  const loadBoardInvoices = useCallback(async () => {
    setBoardRefreshing(true);
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoISO = sevenDaysAgo.toISOString();

      const [activeSnap, recentPickedUpSnap] = await Promise.all([
        getDocs(query(collection(db, 'invoices'), where('status', 'in', ACTIVE_STATUSES))),
        getDocs(query(collection(db, 'invoices'), where('status', '==', 'Picked Up'), where('statusUpdatedAt', '>=', sevenDaysAgoISO))),
      ]);

      let invoices = [
        ...activeSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        ...recentPickedUpSnap.docs.map(d => ({ ...d.data(), id: d.id })),
      ];

      if (isSalesperson) {
        invoices = invoices.filter(inv => inv.salesperson === roleName);
      }

      const board = emptyBoard();
      invoices.forEach(inv => {
        let status = inv.status || 'Ordered';
        if (!board[status]) status = 'Ordered';
        board[status].push(inv);
      });
      setBoardInvoices(board);
      loadBoardDrawings(invoices.map(inv => inv.id));
    } catch (err) {
      console.error('Error loading board invoices:', err);
    }
    setBoardRefreshing(false);
  }, [isSalesperson, roleName]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Linked drawings for the whole board ──────────────────────────────
  //
  // ONE QUERY FOR THE BOARD, NOT ONE PER CARD.
  //
  // Drawings point at documents, not the other way round, so a card cannot know
  // its drawings without asking. Asking per card is N round trips for N cards —
  // forty on a busy board, every one of them a separate latency — and most
  // return nothing. Instead the ids are batched into a single `in` query.
  //
  // Firestore caps `in` at 30 values, so a board of up to 30 cards is ONE
  // query and a board of 60 is two. Chunked rather than fetching the whole
  // collection unfiltered: this transfers only the drawings that are actually
  // linked to something on screen, and stays that way however large the
  // drawings collection grows.
  //
  // NO COMPOSITE INDEX. A single equality filter on linkedDocId and nothing
  // else — no orderBy in the query. The sort happens below, client-side, which
  // is the same shape CustomerProfile uses to fetch a customer's drawings.
  const loadBoardDrawings = async (invoiceIds) => {
    const ids = (invoiceIds || []).filter(Boolean);
    if (ids.length === 0) { setBoardDrawings({}); return; }
    try {
      const chunks = [];
      for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));
      const results = await Promise.all(chunks.map(chunk => getDocs(query(
        collection(db, 'shopDrawings'),
        where('linkedDocId', 'in', chunk),
      ))));
      const byDoc = {};
      results.forEach(snap => snap.docs.forEach(d => {
        const row = { id: d.id, ...d.data() };
        if (!row.linkedDocId) return;
        (byDoc[row.linkedDocId] || (byDoc[row.linkedDocId] = [])).push(row);
      }));
      // Client-side sort, so the query stays single-field.
      Object.values(byDoc).forEach(list =>
        list.sort((a, b) => String(a.drawingNumber || '').localeCompare(String(b.drawingNumber || ''))));
      setBoardDrawings(byDoc);
    } catch (err) {
      // A failure here must not take the board down with it — the cards are the
      // point, the drawing badge is an extra.
      console.error('Error loading linked drawings:', err);
      setBoardDrawings({});
    }
  };

  useEffect(() => {
    if (!roleLoading) loadBoardInvoices();
  }, [roleLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Which cards match, and where they are. Computed once for the whole board
  // rather than per card, so the count and the highlight can never disagree —
  // and so the summary can say WHICH columns to look in, which on a seven
  // column board is most of the value.
  const boardMatches = useMemo(() => {
    const q = boardSearch.trim();
    if (!q) return { ids: new Set(), total: 0, byColumn: [] };
    const ids = new Set();
    const byColumn = [];
    COLUMNS.forEach(col => {
      const hits = (boardInvoices[col.id] || []).filter(inv => boardCardMatches(inv, q));
      hits.forEach(inv => ids.add(inv.id));
      if (hits.length) byColumn.push({ label: col.label, count: hits.length });
    });
    return { ids, total: ids.size, byColumn };
  }, [boardInvoices, boardSearch]);

  // ── Open a specific card asked for from elsewhere ──
  // The notification bell navigates here with an invoiceId in navigation state.
  // This mirrors how ShopDrawings consumes location.state.drawingId: wait until
  // the board has loaded before deciding (otherwise the lookup misses and the
  // link appears to do nothing), open the match, and clear the state afterwards
  // so navigating Back and returning does not force the card open again. A
  // missing id — deleted invoice, or one filtered out of this user's board —
  // simply leaves the board as it is.
  const consumedInvoiceIdRef = useRef(null);
  const requestedInvoiceId = location.state?.invoiceId || null;

  useEffect(() => {
    if (!requestedInvoiceId || consumedInvoiceIdRef.current === requestedInvoiceId) return;
    if (roleLoading || boardRefreshing) return;
    consumedInvoiceIdRef.current = requestedInvoiceId;

    const match = Object.values(boardInvoices).flat().find(inv => inv.id === requestedInvoiceId);
    if (match) setSelectedCard(match);

    navigate(location.pathname, { replace: true, state: null });
  }, [requestedInvoiceId, roleLoading, boardRefreshing, boardInvoices, navigate, location.pathname]);

  // Role-filtered computed values — while roleLoading show empty to avoid flash
  const todayQuotes = !roleLoading
    ? (isAdmin ? rawTodayQuotes : rawTodayQuotes.filter(d => d.salesperson === roleName))
    : [];

  const todayInvoices = !roleLoading
    ? (isAdmin ? rawTodayInvoices : rawTodayInvoices.filter(d => d.salesperson === roleName))
    : [];

  // Stock alerts — same logic as Reports.js: split inventory-tracked products
  // into out-of-stock and low-stock (at/below reorder point).
  const stockAlerts = useMemo(() => {
    const outOfStock = [];
    const lowStock = [];
    products.forEach(p => {
      if (!p.trackInventory) return;
      const stock = parseFloat(p.stockQty) || 0;
      const reorder = parseFloat(p.reorderPoint) || 0;
      if (stock <= 0) {
        outOfStock.push({ ...p, stockQty: stock, reorderPoint: reorder });
      } else if (stock <= reorder) {
        lowStock.push({ ...p, stockQty: stock, reorderPoint: reorder });
      }
    });
    outOfStock.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
    lowStock.sort((a, b) => (a.stockQty / (a.reorderPoint || 1)) - (b.stockQty / (b.reorderPoint || 1)));
    return { outOfStock, lowStock };
  }, [products]);

  const toggleTask = (i) =>
    setTasks(prev => prev.map((t, idx) => idx === i ? { ...t, done: !t.done } : t));

  const updateTaskLabel = (i, label) =>
    setTasks(prev => prev.map((t, idx) => idx === i ? { ...t, label } : t));

  const deleteTask = (i) =>
    setTasks(prev => prev.filter((_, idx) => idx !== i));

  const addTask = () =>
    setTasks(prev => [...prev, { label: '', done: false }]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) navigate(`/documents?search=${encodeURIComponent(searchTerm.trim())}`);
  };

  // Load announcements and this user's dismissals together, then decide whether
  // anything is unseen. displayName is the key, so this waits for it — before
  // auth resolves there is no user to have seen anything.
  useEffect(() => {
    if (!displayName) return;
    let cancelled = false;
    (async () => {
      try {
        const [list, dismissed] = await Promise.all([
          fetchActiveAnnouncements(),
          fetchDismissedIds(displayName),
        ]);
        if (cancelled) return;
        setAnnouncements(list);
        setDismissedIds(dismissed);
        const unseen = unseenAnnouncements(list, dismissed);
        if (unseen.length > 0) setPopupItems(unseen);
      } catch (err) {
        // The Dashboard must render without announcements.
        console.error('Error loading announcements:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [displayName]);

  // Dismissing marks it seen for THIS user only. The announcement stays in the
  // card — dismissing the popup is "I have read it", not "hide it from me".
  const handleDismissAnnouncement = (id) => {
    setDismissedIds(prev => (prev.includes(id) ? prev : [...prev, id]));
    dismissAnnouncement(displayName, id).catch(err => {
      console.error('Could not record announcement dismissal:', err);
    });
  };

  // ── Card modal handlers ──
  // ShopDrawings already opens a specific drawing from navigation state — the
  // notification bell uses exactly this. Reused rather than invented: a second
  // mechanism would be a second thing to keep working.
  const openDrawing = (drawingId) => {
    navigate('/shop-drawings', { state: { drawingId } });
  };

  const handleCardClick = (invoice) => {
    setSelectedCard(invoice);
  };

  const handleStatusChangeInModal = async (invoice, newStatus) => {
    if ((invoice.status || 'Ordered') === newStatus) return;
    const now = new Date().toISOString();
    const currentStatus = invoice.status || 'Ordered';

    try {
      await updateDoc(doc(db, 'invoices', invoice.id), { status: newStatus, statusUpdatedAt: now });
    } catch (err) {
      console.error('Error updating status:', err);
      return;
    }

    const updated = { ...invoice, status: newStatus, statusUpdatedAt: now };

    setBoardInvoices(prev => {
      const next = { ...prev, [currentStatus]: prev[currentStatus].filter(i => i.id !== invoice.id) };
      next[newStatus] = [updated, ...next[newStatus]];
      return next;
    });

    setSelectedCard(updated);
  };

  // Appends one comment to the card's boardComments array.
  //
  // This used to read the document, spread the array it got back, append, and
  // write the WHOLE array. Two people commenting on the same card in the same
  // moment both read the same "before" array, and whichever wrote second
  // overwrote the first — one comment silently gone, with nothing to show it had
  // ever existed. arrayUnion closes that window entirely: the append happens
  // server-side against whatever the array holds at write time, so there is no
  // read to go stale. The getDoc is gone with it.
  // One notification document per recipient, in the flat top-level
  // `notifications` collection — the same shape as payments / inventoryMovements
  // / shopDrawings: no subcollections, ISO string timestamps, and a single
  // equality field (`recipient`) to query on so no composite index is needed.
  //
  // Two rules decide who gets what:
  //   - The author never notifies themselves, by either route. Mentioning
  //     yourself, or commenting on your own job, is not news.
  //   - A mention outranks being the salesperson. Someone who is both gets ONE
  //     notification, typed 'mention', because that is the more specific fact.
  //     The Map below is what enforces it: mentions are inserted first and the
  //     salesperson is only added if the name is not already spoken for.
  const writeCommentNotifications = async (invoice, comment) => {
    const actor = comment.author;
    const recipients = new Map();

    (comment.mentions || []).forEach(name => {
      if (name && name !== actor) recipients.set(name, 'mention');
    });

    // Documents store the salesperson in the same uppercased form, but older
    // records are normalised here rather than trusted.
    const salesperson = String(invoice?.salesperson || '').trim().toUpperCase();
    if (salesperson && salesperson !== actor && !recipients.has(salesperson)) {
      recipients.set(salesperson, 'comment');
    }

    if (recipients.size === 0) return;

    await Promise.all([...recipients.entries()].map(([recipient, type]) =>
      addDoc(collection(db, 'notifications'), {
        recipient,
        actor,
        type,
        invoiceId: invoice.id,
        docNumber: invoice.docNumber || '',
        customerName: invoice.customerName || '',
        text: comment.text,
        createdAt: comment.createdAt,
        readAt: null,
      })
    ));
  };

  const handleAddComment = async (invoice, text) => {
    const invoiceId = invoice.id;
    // See newCommentId: the id is what stops arrayUnion's deep-equality
    // deduplication from swallowing a double-clicked duplicate submission.
    // Nothing else about the comment's shape changed.
    // Resolved against the LIVE list, so a person added in Settings is a real
    // notification rather than plain text.
    const mentions = parseMentions(text, mentionableNames);
    const newComment = {
      id: newCommentId(),
      text,
      author: displayName,
      createdAt: new Date().toISOString(),
      // Omitted entirely when nobody was mentioned, so a plain comment keeps
      // the exact shape it has always had and old comments stay comparable.
      ...(mentions.length > 0 ? { mentions } : {}),
    };

    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);

      await updateDoc(invoiceRef, { boardComments: arrayUnion(newComment) });

      // Local state now APPENDS to whatever this client already had, rather than
      // reusing an array read back from the server — with arrayUnion there is no
      // such array to reuse. The result is the same for the person commenting:
      // their comment shows immediately.
      //
      // What this local copy cannot know is a comment another user added since
      // this client last loaded the board; that one is on the server and will
      // appear on the next refresh. That is a stale VIEW, not a lost write — the
      // old code would have deleted that comment outright.
      setBoardInvoices(prev => {
        const next = { ...prev };
        for (const col in next) {
          next[col] = next[col].map(inv =>
            inv.id === invoiceId
              ? { ...inv, boardComments: [...(inv.boardComments || []), newComment] }
              : inv
          );
        }
        return next;
      });

      // The open card is its own state slot, seeded from the board object on
      // click and never re-derived from it, so it takes its own single append.
      setSelectedCard(prev => prev?.id === invoiceId
        ? { ...prev, boardComments: [...(prev.boardComments || []), newComment] }
        : prev);

      // Notifications are a courtesy; the comment is the record. They are
      // written after the comment is safely stored and on screen, and their
      // own try/catch means a permissions problem or an offline moment can
      // never turn a saved comment into a visible failure.
      try {
        await writeCommentNotifications(invoice, newComment);
      } catch (notifyErr) {
        console.error('Comment saved, but notifications failed:', notifyErr);
      }
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  // Deleting one of YOUR OWN comments. Permanent: no soft-delete flag, no
  // "comment deleted" placeholder left behind.
  //
  // `index` is the position in the list the user is looking at, and it exists
  // only for the legacy path below — the id path does not need it.
  const handleDeleteComment = async (invoice, comment, index) => {
    // The UI already hides the control on other people's comments; this is the
    // rule itself, so nothing that reaches this function by another route can
    // delete a comment its author did not write.
    if (!invoice || !comment || comment.author !== displayName) return;
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;

    const invoiceId = invoice.id;
    const localList = invoice.boardComments || [];

    // For a legacy comment, WHICH of the identical copies this is. Counted from
    // the list on screen and reused against the stored array below, so the one
    // the user clicked is the one that goes.
    const occurrence = comment.id
      ? 0
      : localList.slice(0, index).filter(c => sameLegacyComment(c, comment)).length;

    // Drop the same single element from a local copy of the array.
    const removeFrom = (list) => {
      const arr = list || [];
      if (comment.id) return arr.filter(c => c.id !== comment.id);
      let seen = -1;
      return arr.filter(c => {
        if (!sameLegacyComment(c, comment)) return true;
        seen += 1;
        return seen !== occurrence;
      });
    };

    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);

      if (comment.id) {
        // The happy path, and the mirror of the arrayUnion that added it.
        // arrayRemove matches by deep equality, and the id makes this object
        // unique within the array, so exactly one element can ever match — no
        // read, and therefore no read-modify-write window to lose a write in.
        await updateDoc(invoiceRef, { boardComments: arrayRemove(comment) });
      } else {
        // LEGACY COMMENTS ONLY — those written before the id field existed,
        // carrying just text, author and createdAt.
        //
        // arrayRemove would be wrong here. It matches by deep equality and
        // removes EVERY matching element, and two legacy comments can be
        // genuinely identical: same person, same words, same millisecond — a
        // double-clicked submit under the old code produced exactly that.
        // Deleting one would silently take both.
        //
        // Firestore has no "remove the element at index N", so this one case
        // falls back to a read-modify-write and rebuilds the array without the
        // chosen occurrence. That reintroduces the lost-update window the add
        // path was fixed to avoid, which is why it is scoped to legacy
        // comments alone: every comment written from now on has an id and
        // takes the branch above.
        const snap = await getDoc(invoiceRef);
        const stored = snap.data()?.boardComments || [];
        const next = removeFrom(stored);

        if (next.length === stored.length) {
          // The comment is not in the stored array — already deleted from
          // another session, most likely. Nothing to write.
          console.warn('Comment already gone from Firestore; nothing removed.');
        } else {
          await updateDoc(invoiceRef, { boardComments: next });
        }
      }

      // Local state after the write lands, the same way adding a comment does
      // it — so the row disappears from the modal, and the 💬 count on the
      // board card behind it drops, without waiting for a refresh.
      setBoardInvoices(prev => {
        const nextBoard = { ...prev };
        for (const col in nextBoard) {
          nextBoard[col] = nextBoard[col].map(inv =>
            inv.id === invoiceId ? { ...inv, boardComments: removeFrom(inv.boardComments) } : inv
          );
        }
        return nextBoard;
      });

      setSelectedCard(prev => prev?.id === invoiceId
        ? { ...prev, boardComments: removeFrom(prev.boardComments) }
        : prev);

      // Notifications this comment created are deliberately left alone. Each
      // one carries its own copy of the text, so it still renders correctly,
      // and quietly deleting something out of someone else's bell is worse
      // than leaving a note about a comment that no longer exists.
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  // Quick action card accent colors (per spec — different color top edge per card)
  const QUICK_ACTIONS = [
    { key: 'quote',     icon: '📄', title: 'New Quote',     sub: 'Create a sale quote',     path: '/new-quote',  accent: C_CYAN },
    { key: 'invoice',   icon: '🧾', title: 'New Invoice',   sub: 'Generate an invoice',     path: '/new-invoice', accent: C_VIOLET },
    { key: 'customer',  icon: '🔍', title: 'Find Customer', sub: 'Search customer records', path: '/customers',  accent: '#0096C7' },
    { key: 'pricelist', icon: '💲', title: 'Price List',    sub: 'View & manage prices',    path: '/pricelist',  accent: '#023E8A' },
  ];

  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      background: C_BG,
      transition: 'background 0.3s ease, color 0.3s ease',
      color: C_TEXT,
    }}>
      <style>{`
        .dash-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
        .dash-scroll::-webkit-scrollbar-track { background: rgba(3,4,94,0.03); }
        .dash-scroll::-webkit-scrollbar-thumb { background: rgba(0, 119, 182, 0.3); border-radius: 3px; }
        .dash-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0, 119, 182, 0.5); }
        .dash-grad-text { color: #0077B6; }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6); }
          50% { opacity: 0.7; box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
        }
        .online-dot { animation: pulse-dot 2s infinite; }

        /* ── Today's Documents + What's New ──
           Three tiers, chosen by measuring the densest row rather than by
           reaching for equal thirds.

           The content column is viewport - SIDEBAR_W (200) - 64 (main padding).
           The binding constraint is an invoice row: its top line is
           #docNumber (monospace 12) + a SALE/INVOICE pill + a DEDUCTED/PENDING
           pill on ONE flex line with no wrap, and the amount sits beside it.
           Worst case that is about 197px of number and pills, 77px of amount
           and 8px between them = 282px of content, plus 28px row padding and
           48px card padding = about 358px of card. Below that the flex line
           cannot wrap, so the pills shrink and their labels break inside
           themselves, which looks broken rather than tight.

             equal thirds at 1280 -> (976-48)/3 = 309px card. Crushed.
             1.2/1.2/1fr at 1280 -> 327px document card. Still short of 358.

           So three across waits for 1536, where 1.2/1.2/1fr gives the document
           cards 418px and What's New 348px. Equal thirds would also fit there
           (394px each), but the document cards are the constrained ones and
           What's New carries reflowing text that is happy narrow — so the
           width goes where it is actually needed. */
        .dash-today-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 24px;
        }
        @media (min-width: 1024px) {
          .dash-today-grid { grid-template-columns: 1fr 1fr; }
          /* What's New spans the full width under the two document cards. */
          .dash-today-grid > :nth-child(3) { grid-column: 1 / -1; }
        }
        @media (min-width: 1536px) {
          .dash-today-grid { grid-template-columns: 1.2fr 1.2fr 1fr; }
          .dash-today-grid > :nth-child(3) { grid-column: auto; }
        }

        /* ── Board heading row ──
           Heading left, search and Refresh right, on ONE line — the same shape
           the page header uses for its greeting and its search form.

           Below 900px there is not room for a heading, a 260px field, a match
           count and a button side by side, so the row becomes two: heading
           first, because it says what you are looking at, then the tools
           right-aligned beneath it. */
        .dash-board-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 20px;
        }
        .dash-board-tools {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }
        @media (max-width: 900px) {
          .dash-board-head { flex-direction: column; align-items: stretch; }
          .dash-board-tools { justify-content: flex-end; flex-wrap: wrap; }
        }
      `}</style>

      {/* ── Sidebar ── */}
      <Sidebar />

      {/* ── Main ── */}
      <div style={{ marginLeft: SIDEBAR_W, flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

        {/* Header */}
        <header style={{
          padding: '20px 32px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 10,
          // Solid baby blue matching the page background (C_BG) so the sticky top
          // bar is one uniform shade with the rest of the app — no frosted/translucent
          // tint that read as a different (pinkish) shade over the baby-blue body.
          background: C_BG,
          borderBottom: `1px solid ${C_BORDER}`,
          transition: 'background 0.3s ease',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: C_TEXT, margin: 0 }}>
                {greeting}, <span className="dash-grad-text">{displayName}!</span>
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12, background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                <div className="online-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: C_GREEN }} />
                <span style={{ color: C_GREEN, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Online</span>
              </div>
            </div>
            <p style={{ color: C_TEXT_2, fontSize: 13, marginTop: 4, marginBottom: 0 }}>{dateStr}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <form onSubmit={handleSearch} style={{ display: 'flex' }}>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search quotes, invoices, customers..."
                style={{
                  width: 288,
                  background: C_BG_NESTED, color: C_TEXT,
                  border: `1px solid ${C_BORDER}`, borderRight: 'none',
                  borderTopLeftRadius: 10, borderBottomLeftRadius: 10,
                  padding: '10px 14px', fontSize: 13,
                  outline: 'none',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                }}
                onFocus={e => {
                  e.target.style.borderColor = C_BORDER_CYAN;
                  e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)';
                }}
                onBlur={e => {
                  e.target.style.borderColor = C_BORDER;
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                type="submit"
                style={{
                  background: GRADIENT_HERO, color: C_TEXT,
                  border: 'none',
                  borderTopRightRadius: 10, borderBottomRightRadius: 10,
                  padding: '10px 16px', fontSize: 14, cursor: 'pointer',
                }}
              >
                🔍
              </button>
            </form>
          </div>
        </header>

        <main style={{ flex: 1, padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Quick Action Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {QUICK_ACTIONS.map(card => {
              const isHovered = hoveredCard === card.key;
              return (
                <button
                  key={card.key}
                  onClick={() => navigate(card.path)}
                  onMouseEnter={() => setHoveredCard(card.key)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    background: C_BG_CARD,
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: `1px solid ${isHovered ? card.accent + '4D' : C_BORDER}`,
                    borderRadius: 12,
                    padding: 16,
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transform: isHovered ? 'translateY(-4px)' : 'none',
                    boxShadow: isHovered ? `0 16px 48px ${card.accent}1A` : 'none',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                    color: C_TEXT,
                  }}
                >
                  {/* Top accent glow */}
                  <div style={{
                    position: 'absolute', top: 0, left: '20%', right: '20%',
                    height: 1,
                    background: `linear-gradient(90deg, transparent, ${card.accent}, transparent)`,
                    opacity: isHovered ? 1 : 0.6,
                    transition: 'opacity 0.3s ease',
                  }} />
                  {/* Icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: `linear-gradient(135deg, ${card.accent}33, ${C_VIOLET}33)`,
                    border: `1px solid ${card.accent}4D`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, marginBottom: 10,
                  }}>
                    {card.icon}
                  </div>
                  <h3 style={{ color: C_TEXT, fontSize: 14, fontWeight: 700, margin: 0 }}>{card.title}</h3>
                  <p style={{ color: C_TEXT_2, fontSize: 11, marginTop: 2, marginBottom: 0 }}>{card.sub}</p>
                </button>
              );
            })}
          </div>

          {/* Today's Documents + What's New — proportions and breakpoints are
              in .dash-today-grid in the style block above. */}
          <div className="dash-today-grid">
            {/* Quotes */}
            <div style={{
              background: C_BG_CARD,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: `1px solid ${C_BORDER}`,
              borderRadius: 20, padding: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2 style={{ color: C_TEXT, fontWeight: 700, fontSize: 16, margin: 0 }}>
                  📄 Today's Quotes
                  {isSalesperson && <span style={{ color: C_TEXT_3, fontWeight: 400, fontSize: 13, marginLeft: 6 }}>(yours)</span>}
                </h2>
                {!loadingDocs && !roleLoading && todayQuotes.length > 0 && (
                  <span style={{
                    background: '#E6F1FB',
                    color: C_CYAN_BRIGHT,
                    fontSize: 12, fontWeight: 700,
                    padding: '2px 10px', borderRadius: 12,
                  }}>{todayQuotes.length}</span>
                )}
              </div>
              {loadingDocs || roleLoading ? (
                <p style={{ color: C_TEXT_3, fontSize: 13, margin: 0 }}>Loading…</p>
              ) : todayQuotes.length === 0 ? (
                <p style={{ color: C_TEXT_3, fontSize: 13, margin: 0 }}>No quotes today.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {todayQuotes.map(d => (
                    <div
                      key={d.id}
                      onClick={() => navigate(`/edit-document/quote/${d.id}`)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', borderRadius: 10,
                        background: C_BG_NESTED,
                        border: '1px solid transparent',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s ease, transform 0.2s ease',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = C_BORDER_CYAN;
                        e.currentTarget.style.transform = 'translateX(2px)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div>
                        <p style={{ color: C_CYAN_BRIGHT, fontFamily: 'monospace', fontWeight: 700, fontSize: 12, margin: 0 }}>#{d.docNumber}</p>
                        <p style={{ color: C_TEXT, fontWeight: 600, fontSize: 13, margin: 0 }}>{d.customerName}</p>
                        <p style={{ color: C_TEXT_3, fontSize: 11, margin: 0 }}>{d.salesperson}</p>
                      </div>
                      <p style={{ color: C_GREEN, fontWeight: 700, fontSize: 14, margin: 0 }}>${parseFloat(d.grandTotal || 0).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Invoices */}
            <div style={{
              background: C_BG_CARD,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: `1px solid ${C_BORDER}`,
              borderRadius: 20, padding: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2 style={{ color: C_TEXT, fontWeight: 700, fontSize: 16, margin: 0 }}>
                  🧾 Today's Invoices
                  {isSalesperson && <span style={{ color: C_TEXT_3, fontWeight: 400, fontSize: 13, marginLeft: 6 }}>(yours)</span>}
                </h2>
                {!loadingDocs && !roleLoading && todayInvoices.length > 0 && (
                  <span style={{
                    background: '#E0F7FA',
                    color: C_VIOLET_BRIGHT,
                    fontSize: 12, fontWeight: 700,
                    padding: '2px 10px', borderRadius: 12,
                  }}>{todayInvoices.length}</span>
                )}
              </div>
              {loadingDocs || roleLoading ? (
                <p style={{ color: C_TEXT_3, fontSize: 13, margin: 0 }}>Loading…</p>
              ) : todayInvoices.length === 0 ? (
                <p style={{ color: C_TEXT_3, fontSize: 13, margin: 0 }}>No invoices today.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {todayInvoices.map(d => {
                    // Account customers show "INVOICE"; regular (default) show "SALE".
                    // Snapshot-first, then live customer lookup, else default regular.
                    const acct = d.customerAccount != null
                      ? d.customerAccount
                      : (customers.find(c => c.id === d.customerId)?.isAccountCustomer ?? false);
                    return (
                    <div
                      key={d.id}
                      onClick={() => navigate(`/edit-document/invoice/${d.id}`)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', borderRadius: 10,
                        background: C_BG_NESTED,
                        border: '1px solid transparent',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s ease, transform 0.2s ease',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = 'rgba(0, 180, 216, 0.45)';
                        e.currentTarget.style.transform = 'translateX(2px)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <p style={{ color: C_VIOLET_BRIGHT, fontFamily: 'monospace', fontWeight: 700, fontSize: 12, margin: 0 }}>#{d.docNumber}</p>
                          <span style={{
                            fontSize: 9, fontWeight: 800, letterSpacing: 0.5, padding: '1px 6px', borderRadius: 4,
                            background: acct ? `${C_VIOLET}1A` : '#E6F7EE',
                            color: acct ? C_VIOLET_BRIGHT : '#0F6E56',
                          }}>{acct ? 'INVOICE' : 'SALE'}</span>
                          {(() => {
                            const deducted = resolveInventoryStatus(d) === 'deducted';
                            return (
                              <span style={{
                                fontSize: 9, fontWeight: 800, letterSpacing: 0.5, padding: '1px 6px', borderRadius: 4,
                                background: deducted ? '#E8F5F0' : '#FDEAEA',
                                color: deducted ? '#0F6E56' : '#A32D2D',
                              }}>{deducted ? '🟢 DEDUCTED' : '🔴 PENDING'}</span>
                            );
                          })()}
                        </div>
                        <p style={{ color: C_TEXT, fontWeight: 600, fontSize: 13, margin: 0 }}>{d.customerName}</p>
                        <p style={{ color: C_TEXT_3, fontSize: 11, margin: 0 }}>{d.salesperson}</p>
                      </div>
                      <p style={{ color: C_GREEN, fontWeight: 700, fontSize: 14, margin: 0 }}>${parseFloat(d.grandTotal || 0).toFixed(2)}</p>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* What's New */}
            <div style={{
              background: C_BG_CARD,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: `1px solid ${C_BORDER}`,
              borderRadius: 20, padding: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2 style={{ color: C_TEXT, fontWeight: 700, fontSize: 16, margin: 0 }}>📣 What's New</h2>
                {announcements.length > 0 && (
                  <span style={{
                    background: '#E6F1FB', color: C_CYAN_BRIGHT,
                    fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 12,
                  }}>{announcements.length}</span>
                )}
              </div>
              {announcements.length === 0 ? (
                <p style={{ color: C_TEXT_3, fontSize: 13, margin: 0 }}>No updates.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {announcements.slice(0, ANNOUNCEMENTS_ON_CARD).map(a => {
                    // Unread = never dismissed by this user. A dismissed one
                    // stays listed, just without the accent — the card is the
                    // reference copy, not a queue that empties.
                    const unread = !dismissedIds.includes(a.id);
                    return (
                      <div
                        key={a.id}
                        onClick={() => setReadingAnnouncement(a)}
                        title="Read the full announcement"
                        style={{
                          padding: '10px 14px', borderRadius: 10,
                          background: unread ? '#E6F1FB' : C_BG_NESTED,
                          borderLeft: unread ? `3px solid ${C_CYAN_BRIGHT}` : '3px solid transparent',
                          border: '1px solid transparent',
                          borderLeftWidth: 3,
                          cursor: 'pointer',
                          transition: 'border-color 0.2s ease, transform 0.2s ease',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.borderColor = C_BORDER_CYAN;
                          e.currentTarget.style.transform = 'translateX(2px)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.borderColor = 'transparent';
                          e.currentTarget.style.borderLeftColor = unread ? C_CYAN_BRIGHT : 'transparent';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                          <p style={{ color: C_TEXT, fontWeight: 700, fontSize: 13, margin: 0 }}>{a.title}</p>
                          <span style={{ color: C_TEXT_3, fontSize: 11, flexShrink: 0 }}>{relativeTime(a.createdAt)}</span>
                        </div>
                        {/* PREVIEW ONLY — three lines, then click to read it
                            all. Line breaks are deliberately FLATTENED here:
                            the modal keeps them, but in a 3-line clamp a blank
                            line between paragraphs burns a third of the preview
                            on nothing, so a multi-paragraph update showed one
                            line of text and two of whitespace. Flattening buys
                            three lines of actual words. */}
                        <p style={{
                          color: C_TEXT_2, fontSize: 12, lineHeight: 1.45, margin: '2px 0 0 0',
                          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>{previewText(a.body)}</p>
                      </div>
                    );
                  })}
                  {announcements.length > ANNOUNCEMENTS_ON_CARD && (
                    <p style={{ color: C_TEXT_3, fontSize: 11, margin: '4px 0 0 0', textAlign: 'center' }}>
                      +{announcements.length - ANNOUNCEMENTS_ON_CARD} older not shown
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Invoice Status Board (full width) */}
          <div style={{
            background: C_BG_CARD,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${C_BORDER}`,
            borderRadius: 20, padding: 24,
          }}>
            {/* ── HEADING ROW ──
                Heading left, tools right — the same shape the page header uses
                for its greeting and its search form, so the two read as one
                pattern rather than two.

                THE SEARCH BOX MOVED UP HERE from a row of its own. That row
                cost about 50px of height on the one screen whose whole job is
                showing cards, to hold a control that is perfectly happy beside
                a heading. It is a fixed 260 wide rather than flexible: stretched
                across the remaining width it read as a second heading, which is
                exactly what it is not.

                .dash-board-head stacks below 900px so the board stays usable on
                a small screen; the heading comes first because it says what you
                are looking at. */}
            <div className="dash-board-head">
              <div>
                <h2 style={{ color: C_TEXT, fontWeight: 700, fontSize: 18, margin: 0 }}>📋 Invoice Status Board</h2>
                {isSalesperson && <p style={{ color: C_TEXT_3, fontSize: 12, marginTop: 4, marginBottom: 0 }}>Showing your invoices</p>}
              </div>

              <div className="dash-board-tools">
                {/* The count. It shows the TOTAL, and carries the per-column
                    breakdown in its tooltip rather than on the row: spelled out
                    in full, "8 matches — 1 in Not Ordered / Pending, 2 in
                    Ordered / Placed, …" runs past 900px and would push the
                    search box off the end of a row it now has to share. */}
                {boardSearch.trim() && (
                  <span
                    title={boardMatches.total
                      ? boardMatches.byColumn.map(c => `${c.count} in ${c.label.replace(/^\S+\s+/, '')}`).join(', ')
                      : undefined}
                    style={{
                      fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                      color: boardMatches.total ? BOARD_MATCH_INK : C_TEXT_3,
                    }}
                  >
                    {boardMatches.total === 0
                      ? 'No matches'
                      : `${boardMatches.total} match${boardMatches.total === 1 ? '' : 'es'}`}
                  </span>
                )}

                {/* Same field, same forgiving matching, same clear button — only
                    its place on the page changed. */}
                <div style={{ position: 'relative', width: 260, maxWidth: '100%' }}>
                  <span style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                    fontSize: 13, color: C_TEXT_3, pointerEvents: 'none',
                  }}>🔍</span>
                  <input
                    value={boardSearch}
                    onChange={e => setBoardSearch(e.target.value)}
                    placeholder="Find a card — number or customer"
                    aria-label="Search the board"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '9px 28px 9px 34px',
                      background: C_BG_CARD_SOLID,
                      border: `1px solid ${C_BORDER}`,
                      borderRadius: 10,
                      fontSize: 13, fontFamily: 'inherit', color: C_TEXT,
                      outline: 'none',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    }}
                    onFocus={e => {
                      e.target.style.borderColor = C_BORDER_CYAN;
                      e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)';
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = C_BORDER;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {boardSearch && (
                    <button
                      onClick={() => setBoardSearch('')}
                      aria-label="Clear the search"
                      title="Clear"
                      style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: C_TEXT_3, fontSize: 14, lineHeight: 1, padding: 4,
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

              <button
                onClick={loadBoardInvoices}
                disabled={boardRefreshing}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: C_BG_NESTED,
                  border: `1px solid ${C_BORDER}`,
                  borderRadius: 10,
                  padding: '6px 12px',
                  fontSize: 12, fontWeight: 600,
                  color: C_TEXT_2,
                  cursor: boardRefreshing ? 'default' : 'pointer',
                  opacity: boardRefreshing ? 0.5 : 1,
                  transition: 'border-color 0.2s ease, color 0.2s ease',
                }}
                onMouseEnter={e => {
                  if (!boardRefreshing) {
                    e.currentTarget.style.borderColor = C_BORDER_CYAN;
                    e.currentTarget.style.color = C_CYAN_BRIGHT;
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = C_BORDER;
                  e.currentTarget.style.color = C_TEXT_2;
                }}
              >
                <span style={{ display: 'inline-block', animation: boardRefreshing ? 'spin 1s linear infinite' : 'none' }}>🔄</span>
                {boardRefreshing ? 'Refreshing…' : 'Refresh'}
              </button>
              </div>
            </div>

            {roleLoading ? (
              <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C_TEXT_3 }}>Loading…</div>
            ) : (
              <>
                {/* Kanban columns — one per status, all shown side by side */}
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)`, gap: 12 }}>
                  {COLUMNS.map(col => {
                    const cards = boardInvoices[col.id] || [];
                    return (
                      <div key={col.id} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        {/* Column header */}
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                          marginBottom: 10, paddingBottom: 8,
                          borderBottom: `2px solid ${col.color}`,
                        }}>
                          <span style={{
                            color: col.accent, fontSize: 11, fontWeight: 800,
                            textTransform: 'uppercase', letterSpacing: 0.5,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {col.label}
                          </span>
                          <span style={{
                            background: `${col.color}1A`, color: col.accent,
                            padding: '2px 8px', borderRadius: 10,
                            fontSize: 11, fontWeight: 800, minWidth: 22, textAlign: 'center', flexShrink: 0,
                          }}>
                            {cards.length}
                          </span>
                        </div>

                        {/* Cards */}
                        {cards.length === 0 ? (
                          <div style={{
                            padding: '20px 8px', textAlign: 'center',
                            background: C_BG_NESTED,
                            border: `1px dashed ${C_BORDER}`, borderRadius: 10,
                          }}>
                            <p style={{ color: C_TEXT_3, fontSize: 12, margin: 0 }}>None</p>
                          </div>
                        ) : (
                          <div className="dash-scroll" style={{
                            display: 'flex', flexDirection: 'column', gap: 8,
                            maxHeight: 420, overflowY: 'auto', paddingRight: 4,
                          }}>
                            {cards.map(inv => {
                              // A match is marked THREE ways: a heavier border,
                              // a background tint, and a ★ on the card. Across a
                              // seven column board a tint alone is easy to miss,
                              // and a colour alone is no use to anyone reading
                              // this in bad light or without full colour vision.
                              const hit = boardMatches.ids.has(inv.id);
                              return (
                              <div
                                key={inv.id}
                                onClick={() => handleCardClick(inv)}
                                data-match={hit ? 'yes' : undefined}
                                style={{
                                  background: hit ? BOARD_MATCH_BG : '#CDE7F5',
                                  // Longhand, NOT the `border` shorthand: the
                                  // shorthand resets borderLeft every time this
                                  // toggles, which drops the column's colour
                                  // stripe on any card the search touches.
                                  borderTop: hit ? `2px solid ${BOARD_MATCH_INK}` : `1px solid ${C_BORDER}`,
                                  borderRight: hit ? `2px solid ${BOARD_MATCH_INK}` : `1px solid ${C_BORDER}`,
                                  borderBottom: hit ? `2px solid ${BOARD_MATCH_INK}` : `1px solid ${C_BORDER}`,
                                  borderLeft: `4px solid ${col.color}`,
                                  borderRadius: 10,
                                  padding: hit ? '9px 11px' : '10px 12px',
                                  cursor: 'pointer', transition: 'all 0.2s',
                                }}
                                onMouseEnter={e => { const c = hit ? BOARD_MATCH_INK : `${col.color}80`; e.currentTarget.style.borderTopColor = c; e.currentTarget.style.borderRightColor = c; e.currentTarget.style.borderBottomColor = c; }}
                                onMouseLeave={e => { const c = hit ? BOARD_MATCH_INK : C_BORDER; e.currentTarget.style.borderTopColor = c; e.currentTarget.style.borderRightColor = c; e.currentTarget.style.borderBottomColor = c; }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: col.color, flexShrink: 0 }} />
                                  <span style={{ fontFamily: 'monospace', color: C_CYAN_BRIGHT, fontSize: 11, fontWeight: 700 }}>
                                    #{inv.docNumber}
                                  </span>
                                  {hit && (
                                    <span
                                      title="Matches your search"
                                      style={{
                                        color: BOARD_MATCH_INK, fontSize: 10, fontWeight: 800,
                                        letterSpacing: 0.4, flexShrink: 0,
                                      }}
                                    >
                                      ★
                                    </span>
                                  )}
                                  {inv.boardComments && inv.boardComments.length > 0 && (
                                    <span style={{ color: '#0096C7', fontSize: 10, fontWeight: 700, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                                      💬 {inv.boardComments.length}
                                    </span>
                                  )}
                                  {/* Linked drawings — the same treatment as the
                                      comment count beside it, and nothing at all
                                      when there are none. A COUNT, not a row of
                                      text: the board is seven columns of cards
                                      and a line per drawing would cost more
                                      height than the information is worth.

                                      One drawing opens straight to it; several
                                      open the card, which lists them. Going via
                                      the card for a single drawing would be a
                                      pointless extra tap, and guessing which of
                                      three to open would be worse. */}
                                  {(boardDrawings[inv.id] || []).length > 0 && (() => {
                                    const list = boardDrawings[inv.id];
                                    return (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`${list.length} linked drawing${list.length === 1 ? '' : 's'}`}
                                        title={list.length === 1
                                          ? `Open ${list[0].drawingNumber}`
                                          : `${list.length} linked drawings`}
                                        onClick={e => {
                                          e.stopPropagation();   // the card itself opens the modal
                                          if (list.length === 1) openDrawing(list[0].id);
                                          else handleCardClick(inv);
                                        }}
                                        onKeyDown={e => {
                                          if (e.key !== 'Enter' && e.key !== ' ') return;
                                          e.stopPropagation(); e.preventDefault();
                                          if (list.length === 1) openDrawing(list[0].id);
                                          else handleCardClick(inv);
                                        }}
                                        style={{
                                          color: '#5B21B6', fontSize: 10, fontWeight: 700,
                                          marginLeft: (inv.boardComments && inv.boardComments.length > 0) ? 0 : 'auto',
                                          display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
                                        }}
                                      >
                                        📐 {list.length}
                                      </span>
                                    );
                                  })()}
                                </div>
                                {/* Pick status — READ ONLY. The office sends an
                                    invoice to the shop from ViewDocument; the
                                    board is where it watches for "Ready"
                                    without walking to the warehouse. */}
                                {(() => {
                                  const badge = pickBadge(inv);
                                  if (!badge) return null;
                                  return (
                                    <span style={{
                                      display: 'inline-block', marginTop: 3,
                                      background: badge.bg, color: badge.fg,
                                      fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
                                      padding: '1px 6px', borderRadius: 4,
                                    }}>
                                      {badge.icon} {badge.label.toUpperCase()}
                                    </span>
                                  );
                                })()}
                                {/* Back order — a DIFFERENT FACT from the pick
                                    status above it, not a stronger version of
                                    it. Ready says the shop has gathered the
                                    order; this says the customer has not yet
                                    taken all of it. Both can be true, either
                                    can be true alone. Outlined chip with a
                                    count so it never reads as another status
                                    pill. */}
                                {(() => {
                                  if (!hasBackOrder(inv)) return null;
                                  return (
                                    <span
                                      title={`${backOrderCount(inv)} of ${collectableCount(inv)} lines not yet collected`}
                                      style={{
                                        display: 'inline-block', marginTop: 3, marginLeft: 4,
                                        background: 'transparent', color: '#8A3D00',
                                        border: '1.5px solid #C77700',
                                        fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
                                        padding: '0px 5px', borderRadius: 4,
                                      }}
                                    >
                                      ⬚ BACK ORDER {backOrderCount(inv)}
                                    </span>
                                  );
                                })()}
                                <p style={{ color: C_TEXT, fontSize: 12, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {inv.customerName}
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 4 }}>
                                  <span style={{ color: C_TEXT_3, fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {new Date(inv.createdAt || inv.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    {inv.salesperson ? ` · ${inv.salesperson}` : ''}
                                  </span>
                                  <span style={{ color: '#0077B6', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                                    ${(parseFloat(inv.grandTotal) || 0).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* View-all link */}
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button
                    onClick={() => navigate('/documents')}
                    style={{ background: 'none', border: 'none', color: C_TEXT_3, fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    View all documents →
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Daily Tasks (left) + Stock Levels (right) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

          {/* Daily Tasks */}
          <div style={{
            background: C_BG_CARD,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${C_BORDER}`,
            borderRadius: 20, padding: 24,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ color: C_TEXT, fontWeight: 700, fontSize: 18, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: C_CYAN_BRIGHT }}>✓</span> Daily Tasks
              </h2>
              {editingTasks ? (
                <button
                  onClick={() => setEditingTasks(false)}
                  style={{
                    background: GRADIENT_HERO, color: C_TEXT,
                    border: 'none', borderRadius: 8,
                    padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Save
                </button>
              ) : (
                <button
                  onClick={() => setEditingTasks(true)}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${C_VIOLET}66`, borderRadius: 8,
                    padding: '6px 14px', fontSize: 12, fontWeight: 600,
                    color: C_VIOLET_BRIGHT, cursor: 'pointer',
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,180,216,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  title="Edit tasks"
                >
                  ✏️ Edit
                </button>
              )}
            </div>

            {editingTasks ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tasks.map((task, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="text"
                      value={task.label}
                      onChange={e => updateTaskLabel(i, e.target.value)}
                      placeholder="Task name..."
                      style={{
                        flex: 1,
                        background: C_BG_NESTED,
                        border: `1px solid ${C_BORDER}`,
                        borderRadius: 8,
                        padding: '8px 12px', fontSize: 13,
                        color: C_TEXT, outline: 'none',
                      }}
                      onFocus={e => { e.target.style.borderColor = C_BORDER_CYAN; }}
                      onBlur={e => { e.target.style.borderColor = C_BORDER; }}
                    />
                    <button
                      onClick={() => deleteTask(i)}
                      style={{
                        background: 'transparent', border: 'none',
                        color: C_TEXT_3, fontSize: 18, lineHeight: 1, cursor: 'pointer',
                        flexShrink: 0,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = C_RED; }}
                      onMouseLeave={e => { e.currentTarget.style.color = C_TEXT_3; }}
                      title="Remove task"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  onClick={addTask}
                  style={{
                    width: '100%', marginTop: 4,
                    background: 'transparent',
                    border: `1px dashed ${C_BORDER_CYAN}`,
                    borderRadius: 8,
                    padding: '8px 12px', fontSize: 13, fontWeight: 600,
                    color: C_CYAN_BRIGHT, cursor: 'pointer',
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,119,182,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  + Add Task
                </button>
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {tasks.map((task, i) => (
                  <li
                    key={i}
                    onClick={() => toggleTask(i)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                  >
                    <div style={{
                      width: 20, height: 20, borderRadius: 6,
                      border: task.done ? 'none' : `2px solid ${C_BORDER_CYAN}`,
                      background: task.done ? GRADIENT_HERO : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}>
                      {task.done && <span style={{ color: C_TEXT, fontSize: 12, fontWeight: 700 }}>✓</span>}
                    </div>
                    <span style={{
                      fontSize: 14, userSelect: 'none',
                      color: task.done ? C_TEXT_3 : C_TEXT_2,
                      textDecoration: task.done ? 'line-through' : 'none',
                      transition: 'color 0.2s ease',
                    }}>
                      {task.label}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Stock Levels — admin only (replaces the old one-line Low Stock Alert) */}
          {isAdmin && !roleLoading && products.length > 0 && (
            <div style={{
              background: C_BG_CARD,
              border: `1px solid ${C_BORDER}`,
              borderRadius: 20, padding: 24,
            }}>
              <h2 style={{ color: C_TEXT, fontWeight: 700, fontSize: 18, margin: '0 0 16px 0' }}>📦 Stock Levels</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                {/* Out of Stock */}
                <div>
                  <h3 style={{ color: '#A32D2D', fontSize: 15, fontWeight: 800, margin: '0 0 4px 0' }}>🔴 Out of Stock</h3>
                  <p style={{ color: C_TEXT_2, fontSize: 12, margin: '0 0 12px 0' }}>
                    {stockAlerts.outOfStock.length} product{stockAlerts.outOfStock.length !== 1 ? 's' : ''} need{stockAlerts.outOfStock.length === 1 ? 's' : ''} immediate reorder
                  </p>
                  {stockAlerts.outOfStock.length === 0 ? (
                    <p style={{ color: C_TEXT_3, fontSize: 13, textAlign: 'center', padding: 20, background: C_BG_NESTED, borderRadius: 12, margin: 0 }}>✓ All stocked</p>
                  ) : (
                    <div className="dash-scroll" style={{ maxHeight: 400, overflowY: 'auto' }}>
                      {stockAlerts.outOfStock.map(p => <StockProgressRow key={p.id} product={p} type="out" />)}
                    </div>
                  )}
                </div>
                {/* Low Stock */}
                <div>
                  <h3 style={{ color: '#854F0B', fontSize: 15, fontWeight: 800, margin: '0 0 4px 0' }}>🟡 Low Stock</h3>
                  <p style={{ color: C_TEXT_2, fontSize: 12, margin: '0 0 12px 0' }}>
                    {stockAlerts.lowStock.length} product{stockAlerts.lowStock.length !== 1 ? 's' : ''} below reorder point
                  </p>
                  {stockAlerts.lowStock.length === 0 ? (
                    <p style={{ color: C_TEXT_3, fontSize: 13, textAlign: 'center', padding: 20, background: C_BG_NESTED, borderRadius: 12, margin: 0 }}>✓ Nothing low</p>
                  ) : (
                    <div className="dash-scroll" style={{ maxHeight: 400, overflowY: 'auto' }}>
                      {stockAlerts.lowStock.map(p => <StockProgressRow key={p.id} product={p} type="low" />)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          </div>

        </main>
      </div>

      {/* Card Detail Modal */}
      {/* Unseen announcements, shown once per person. Rendered before the card
          modal so it is the first thing a returning user deals with. */}
      {popupItems && popupItems.length > 0 && (
        <AnnouncementModal
          items={popupItems}
          onDismiss={handleDismissAnnouncement}
          onClose={() => setPopupItems(null)}
        />
      )}

      {/* An announcement opened by hand. Same component as the login popup, so
          it looks identical — but readOnly, so nothing is marked seen. */}
      {readingAnnouncement && (
        <AnnouncementModal
          items={[readingAnnouncement]}
          readOnly
          onClose={() => setReadingAnnouncement(null)}
        />
      )}

      {selectedCard && (
        <CardModal
          invoice={selectedCard}
          onClose={() => setSelectedCard(null)}
          drawings={boardDrawings[selectedCard.id] || []}
          onOpenDrawing={(drawingId) => { setSelectedCard(null); openDrawing(drawingId); }}
          onNavigate={(id) => { setSelectedCard(null); navigate(`/edit-document/invoice/${id}`); }}
          onStatusChange={handleStatusChangeInModal}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
          currentUser={displayName}
        />
      )}

      {/* Spin animation for refresh button */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default Dashboard;
